'use client';

import { Component, type ReactNode } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { isProdEnvironment } from '@/utils/environment';

interface ModuleErrorBoundaryProps {
  moduleType: string;
  moduleId?: string;
  children: ReactNode;
}

interface ModuleErrorBoundaryState {
  hasError: boolean;
}

/**
 * Module payloads come from Contentful through a Redis cache that outlives
 * deploys, so a single module can receive data its component can no longer
 * render (e.g. a payload cached before a shape change). One bad module must
 * cost its own section, not the page: the boundary drops it and reports the
 * error to Datadog.
 */
export class ModuleErrorBoundary extends Component<ModuleErrorBoundaryProps, ModuleErrorBoundaryState> {
  state: ModuleErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ModuleErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    if (!isProdEnvironment()) {
      console.error(error);
    }

    datadogRum.addError(error, { moduleType: this.props.moduleType, moduleId: this.props.moduleId });
  }

  render() {
    return this.state.hasError ? null : this.props.children;
  }
}
